import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { isPushSupported, getPushPermission, subscribeToPush, isPushSubscribed } from '../lib/push';

const SHOW_ON_PATHS = ['/dashboard', '/profile'];

export const PushOptInBanner = () => {
  const { isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const evaluate = async () => {
      if (!isAuthenticated || !isPushSupported()) return;
      if (getPushPermission() !== 'default') return; // já concedeu ou bloqueou
      if (sessionStorage.getItem('push_optin_dismissed') === '1') return;
      if (!SHOW_ON_PATHS.includes(location.pathname)) return;
      const already = await isPushSubscribed();
      if (active && !already) setVisible(true);
    };
    evaluate();
    return () => { active = false; };
  }, [isAuthenticated, location.pathname]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      toast.success(language === 'pt' ? 'Notificações ativadas!' : 'Notifications enabled!');
      setVisible(false);
    } catch (e) {
      if (e.message === 'not-granted' || e.message === 'denied') {
        toast.error(language === 'pt' ? 'Permissão de notificações não concedida.' : 'Notification permission not granted.');
      } else {
        toast.error(language === 'pt' ? 'Não foi possível ativar as notificações.' : 'Could not enable notifications.');
      }
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('push_optin_dismissed', '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 md:left-auto md:right-6 md:w-[380px] z-[70] animate-fade-in"
      data-testid="push-optin-banner"
    >
      <div className="bg-white rounded-2xl shadow-xl border border-[#E6E2DA] p-5 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-[#A8A29A] hover:text-[#635E54]"
          data-testid="push-optin-close"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#BFA05F]/10 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-[#BFA05F]" />
          </div>
          <div className="flex-1">
            <p className="font-heading text-sm font-semibold text-[#1A1815]">
              {language === 'pt' ? 'Lembretes no telemóvel' : 'Reminders on your phone'}
            </p>
            <p className="text-xs text-[#635E54] mt-1">
              {language === 'pt'
                ? 'Deseja receber lembretes do seu agendamento direto no seu telemóvel?'
                : 'Would you like to get your appointment reminders directly on your phone?'}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                onClick={handleEnable}
                disabled={loading}
                className="bg-[#1A1815] text-white hover:bg-[#BFA05F] rounded-full h-9 px-4 text-sm"
                data-testid="push-optin-enable"
              >
                {loading
                  ? (language === 'pt' ? 'A ativar...' : 'Enabling...')
                  : (language === 'pt' ? 'Sim, ativar' : 'Yes, enable')}
              </Button>
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="text-[#635E54] hover:bg-[#F1EEE8] rounded-full h-9 px-4 text-sm"
                data-testid="push-optin-later"
              >
                {language === 'pt' ? 'Agora não' : 'Not now'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushOptInBanner;
